"use client";

import { useState } from "react";
import clsx from "clsx";
import type { EventLogState } from "@/lib/sse/useSSE";
import { resolveSignParty, normalisePerspective } from "@/lib/sign/signerParties";
import { inferActiveScenario } from "@/lib/presenter/inferActiveScenario";
import { Dashboard } from "./Dashboard";
import { ProgressBadge } from "./ProgressBadge";
import { CatalogueManager }   from "@/components/scenes/act0/CatalogueManager";
import { AlMadarDiscovery, MinimalPublicProfile } from "@/components/scenes/act0/AlMadarDiscovery";
import { ShareModal }         from "@/components/scenes/act0/ShareModal";
import { WhatsAppMock }       from "@/components/scenes/act0/WhatsAppMock";  // dormant — kept for cohorts that prefer WhatsApp framing
import { EmailMock }          from "@/components/scenes/act0/EmailMock";
import { AlMadarReceives }    from "@/components/scenes/act0/AlMadarReceives"; // dormant — superseded by EmailMock for 0.4
import { PublicCataloguePage} from "@/components/scenes/act0/PublicCataloguePage";
import { ProposalGate }       from "@/components/scenes/act0/ProposalGate";
import { AccountCreation }    from "@/components/scenes/act1/AccountCreation";
import { EntityRegistration } from "@/components/scenes/act1/EntityRegistration";
import { AmlScreen }          from "@/components/scenes/act1/AmlScreen";
import { LiveVerificationCall } from "@/components/scenes/act1/LiveVerificationCall";
import { OrbIdFeeCheckout }   from "@/components/scenes/act1/OrbIdFeeCheckout";
import { OrbIdMaterialisation } from "@/components/scenes/act1/OrbIdMaterialisation";
import { RealTimeCatalogue }  from "@/components/scenes/act1/RealTimeCatalogue";
import { ContractCapture, ContractAndInsuranceSignModals } from "@/components/scenes/act2/ContractCapture";
import { DocumentCarousel, FERTILIZER_DOCS }     from "@/components/scenes/act2/DocumentCarousel";
import { ReconciliationMatrix } from "@/components/scenes/act2/ReconciliationMatrix";
import { InvoiceFsmTile }       from "@/components/scenes/act2/InvoiceFsmTile";
import { SmartLogistics }       from "@/components/scenes/act2/SmartLogistics";
import { ComplianceSnapshot }   from "@/components/scenes/act2/ComplianceSnapshot";
import { TariffEstimateTile }   from "@/components/scenes/shared/TariffTiles";
import { SingleWindowReveal }   from "@/components/scenes/act2/SingleWindowReveal";
import { LcComposer }           from "@/components/scenes/act3/LcComposer";
import { FundingTile }          from "@/components/scenes/act3/FundingTile";
import { GateCascade }          from "@/components/scenes/act3/GateCascade";
import { LcIssued }             from "@/components/scenes/act3/LcIssued";
import { SettlementSimulation } from "@/components/scenes/act3/SettlementSimulation";
import { T1Reveal }             from "@/components/scenes/act3/T1Reveal";
// WelcomeBack moved to lib/scenes/act4 (M1).
// CompressedTrace removed (C6) — toggle deleted; full trace arc plays.
// ReorderDraft + CounterOfferAndAccept moved to lib/scenes/act4 (M1).
// T2Reveal moved to lib/scenes/act4 (M1).
import { LogisticsInsurancePicker } from "@/components/scenes/shared/LogisticsInsurancePicker";
import { BridgeBeat }              from "@/components/scenes/shared/BridgeBeat";
// useCompressedTrace removed (C6).
import { InsuranceCertAnchor }      from "@/components/scenes/shared/InsuranceCertAnchor";
// LcCompositionProgress moved to lib/scenes/act4 (M1) — used only by T2.
import { TariffFinalTile }          from "@/components/scenes/shared/TariffTiles";
import { ReverseCorridorDiscovery } from "@/components/scenes/act5/ReverseCorridorDiscovery";
// T3ContractCompressedTrace removed (C6) — compressed-trace mode deleted.
import { PampagroRfq, GulfPhosphateReply } from "@/components/scenes/act5/PampagroRfq";
import { T3Reveal }                       from "@/components/scenes/act5/T3Reveal";
import { FinalReveal } from "@/components/scenes/act6/FinalReveal";
import { SmartLogisticsImporter } from "@/components/scenes/counterparts/SmartLogisticsImporter";
import { SettlementImporter }     from "@/components/scenes/counterparts/SettlementImporter";
import { TradeInProgressExporter } from "@/components/scenes/counterparts/TradeInProgressExporter";
import { InboundProposalTriage }   from "@/components/scenes/counterparts/InboundProposalTriage";
import { CatalogueAnalytics }      from "@/components/scenes/counterparts/CatalogueAnalytics";
import { LcAdvisedToYou }          from "@/components/scenes/counterparts/LcAdvisedToYou";
import { SplitScreen }        from "./SplitScreen";
import { AttributionStrip }   from "./AttributionStrip";
import { getAttribution }     from "@/lib/attribution/attributions";
import { HANDSHAKE }          from "@/lib/handshakes";
// useUserActions / USER_ACTION moved to lib/scenes/act4 (M1).
import { ContractCaptureWithHandshake } from "@/components/scene/ContractCaptureWithHandshake";
import { pickInvoiceState, lcGateDecisions, resolveCounterpartKind } from "@/components/scene/SceneRouterHelpers";
import { T2_RENDERERS } from "@/lib/scenes/act4";
import { ACT0_RENDERERS } from "@/lib/scenes/act0";
import { ACT1_RENDERERS } from "@/lib/scenes/act1";
import { ACT2_RENDERERS } from "@/lib/scenes/act2";
import { ACT3_RENDERERS } from "@/lib/scenes/act3";
import { ACT5_RENDERERS } from "@/lib/scenes/act5";
import { ACT7_RENDERERS } from "@/lib/scenes/act7";
import { ACT6_RENDERERS } from "@/lib/scenes/act6";
import {
  isAct0Screen, isAct1Screen, isAct2Screen, isAct3Screen,
  isT2Screen, isT3Screen, isT4Screen, isAct6Screen,
} from "@/lib/scenes/screens";

/**
 * SceneRouter selects the right screen based on the latest ui.ScreenChanged
 * event. The persistent Web rail has been retired in favour of inline
 * NetworkVignette components in scenes that have network-relevant beats
 * (1.6 OrbID materialisation · 2.2 document carousel · 3.4/4.5/5.6 LC
 * issued · 6.1 final reveal full-bleed canvas). The `graph` prop is the
 * live network state derived once at the page level via useGraphState.
 */
export function SceneRouter({
  ev,
  graph,
  perspectiveOverride = null,
  className,
}: {
  ev: EventLogState;
  graph: import("@/components/web/types").GraphState;
  perspectiveOverride?: string | null;
  className?: string;
}) {
  const screen = ev.currentScreen?.screen ?? "0.1";
  // The presenter chrome's E/I hotkeys override the scenario-driven
  // perspective for the current screen — Decision 6 (keyboard-only swap).
  const perspective = perspectiveOverride ?? ev.currentScreen?.perspective ?? "E";
  const modules = ev.modulesUnlocked;

  // Dashboard variant + highlight by perspective. Split-screen handshakes
  // skip the dashboard so the two panes can claim the full width.
  // Act 6 also skips the dashboard so the full-bleed Web takes the stage.
  const isSplit = perspective === "split";
  const isImporter = perspective.startsWith("I-");
  const isExporter = perspective === "E" || perspective === "E-GP";
  const isFinalReveal = screen.startsWith("6.");

  // Dashboard is always visible — the audience uses it as the persistent
  // "your modules" reference frame. For split-screen handshakes and the
  // final reveal we still pick a sensible variant (importer, since Al Madar /
  // Pampagro are the protagonists in T1-T2 and T3 respectively).
  const dashboardVariant: "exporter" | "importer" = isExporter ? "exporter" : "importer";

  return (
    <div className={clsx("flex h-full min-h-[640px]", className)}>
      <ProgressBadge screen={ev.currentScreen?.screen} />
      <Dashboard variant={dashboardVariant} unlocked={modules} highlight={pickHighlight(screen)} />

      <div className={clsx("relative min-w-0 flex-1 overflow-hidden", isFinalReveal ? "" : "p-4")}>
        {/* `key` triggers a remount on screen change, fading the new scene
         *  in via .scene-fade. Avoids hard cuts that read as glitches. The
         *  perspective override is part of the key so swap counterparts
         *  also fade in cleanly.
         *
         *  data-screen + data-perspective expose the active scene to
         *  Playwright (L3 E2E suite) without bleeding into runtime
         *  behaviour. Tests wait on these selectors to assert which
         *  scene mounted, rather than racing visual fade-in. */}
        <div
          key={`${screen}::${perspectiveOverride ?? "_"}`}
          data-screen={screen}
          data-perspective={perspectiveOverride ?? ev.currentScreen?.perspective ?? "E"}
          className="scene-fade flex h-full min-h-0 flex-col"
        >
          {(() => {
            // Attribution strip pinned to the top of every action screen,
            // showing "Actioned by: X · Notified: Y, Z". Skipped on the
            // final-reveal full-bleed canvas and on browse-only screens
            // (table returns null).
            const attribution = isFinalReveal ? null : getAttribution(screen);
            return attribution ? <AttributionStrip attribution={attribution} /> : null;
          })()}
          <div className="min-h-0 flex-1">
            <Scene
              screen={screen}
              ev={ev}
              graph={graph}
              naturalPerspective={ev.currentScreen?.perspective ?? "E"}
              perspectiveOverride={perspectiveOverride}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Scene({
  screen, ev, graph, naturalPerspective, perspectiveOverride,
}: {
  screen: string;
  ev: EventLogState;
  graph: import("@/components/web/types").GraphState;
  naturalPerspective: string;
  perspectiveOverride: string | null;
}) {
  // Resolve the signer party set for Contract / Insurance / e-Invoice
  // popups: scenario decides which two parties + which Incoterms (CIF/FOB),
  // perspective decides which one is acting (Seller vs Buyer side).
  const scenarioId = inferActiveScenario(ev);
  const effectivePerspective = perspectiveOverride ?? naturalPerspective ?? "E";
  const signParty = resolveSignParty(scenarioId, normalisePerspective(effectivePerspective));
  const invoiceSignerHeader =
    scenarioId === "t3-reverse-corridor" ? "Einvoice · signed by Gulf Phosphate's Corp DID" :
    scenarioId === "t4-air-cargo"        ? "Einvoice · signed by Verde Carnes' Corp DID"   :
                                           "Einvoice · signed by Aurora's Corp DID";

  // Compressed-trace toggle removed (C6). The full trace arc plays
  // unconditionally — the toggle was a band-aid for pacing debt and
  // presenters didn't trust they could answer follow-ups from the
  // compressed mosaic. The trace screens are the demo's strongest
  // pedagogy; we commit to them.

  // When the presenter has manually overridden perspective AND that
  // override is meaningfully different from the screen's natural side,
  // try to render a counterpart view. Falls through to the natural
  // scene if no counterpart is registered for this combination —
  // chrome (HUD glyph + dashboard variant) still reflects the override.
  if (
    perspectiveOverride &&
    perspectiveOverride !== "split" &&
    perspectiveOverride !== naturalPerspective
  ) {
    const counterpart = pickCounterpart(screen, perspectiveOverride, ev);
    if (counterpart) return counterpart;
  }

  // M1 — every screen is sourced from a per-act renderer registry.
  // Record<ActNScreen, render> forces compile-time exhaustiveness so a
  // scenario JSON renumber that's not mirrored in TypeScript can no
  // longer ship as a silent runtime fallthrough.
  const ctx = { ev, graph, signParty, invoiceSignerHeader };
  if (isAct0Screen(screen)) return ACT0_RENDERERS[screen](ctx);
  if (isAct1Screen(screen)) return ACT1_RENDERERS[screen](ctx);
  if (isAct2Screen(screen)) return ACT2_RENDERERS[screen](ctx);
  if (isAct3Screen(screen)) return ACT3_RENDERERS[screen](ctx);
  if (isT2Screen(screen))   return T2_RENDERERS[screen](ctx);
  if (isT3Screen(screen))   return ACT5_RENDERERS[screen](ctx);
  if (isT4Screen(screen))   return ACT7_RENDERERS[screen](ctx);
  if (isAct6Screen(screen)) return ACT6_RENDERERS[screen](ctx);

  // Legacy fall-through for screens removed from the scenario JSONs but
  // still referenced from manual /control/seek navigation (e.g. 0.2 /
  // 0.4 channel-picker / inbound-email after the N4 cut).
  switch (screen) {
    // Legacy fall-throughs for screens removed from scenario JSONs but
    // still reachable via /control/seek. After M1, every active screen
    // is dispatched above via the per-act registries; these remain only
    // for backwards compatibility with manual navigation.
    case "0.2": return <DiscoveryWithChannelPicker />;
    case "0.4": return <EmailMock variant="inbound" />;

    default:
      return (
        <div className="grid h-full place-items-center">
          <p className="font-mono text-[11px] uppercase tracking-wider text-orbitra-muted">
            Screen {screen} — pending later phase
          </p>
        </div>
      );
  }
}

// DiscoveryWithChannelPicker — retained for the 0.2 legacy /control/seek
// route. Other helpers (Materialisation, CatalogueWithModal,
// pickLvcPhase, pickPaymentState, scenarioReputation*) extracted to
// lib/scenes/act1.tsx and components/scene/SceneRouterHelpers.ts (M1).
function DiscoveryWithChannelPicker() {
  return (
    <div className="relative h-full">
      <MinimalPublicProfile />
      <ShareModal />
    </div>
  );
}

/**
 * Counterpart dispatch: when the presenter manually overrides perspective
 * (E or I) and the override differs from the screen's natural side, we
 * render a counterpart view. Returns null if no counterpart is registered
 * for this combination — caller falls through to the natural scene.
 *
 * Coverage:
 *   importer counterparts (E/E-GP screens viewed as importer):
 *     2.5  → SmartLogisticsImporter
 *     3.5  → SettlementImporter (T1)
 *     5.17 → SettlementImporter (T3 · Itaú · BRL ↔ AED legs)
 *   exporter counterparts (importer screens viewed as exporter):
 *     1.x                    → TradeInProgressExporter (onboarding)
 *     3.1–3.3                → TradeInProgressExporter (escrow-in-progress)
 *     4.10–4.12              → TradeInProgressExporter (lc-in-progress)
 *     5.4 + 5.12–5.14        → TradeInProgressExporter (t3-lc-in-progress)
 *
 *   T2: 4.3 + 4.4 are Aurora-driven (counter-offer; CIF picker) so E falls
 *       through to natural scenes. 4.5 contract capture is split-natural.
 *   T3: 5.4 is Pampagro-driven (FOB picker) → placeholder for E-GP. 5.5
 *       contract capture is split-natural. 5.12–5.14 mirror T2's
 *       LC-composition arc.
 *
 * Everything else falls through to the natural scene with the chrome
 * (HUD glyph + dashboard variant) reflecting the override.
 */
function pickCounterpart(screen: string, perspective: string, ev: EventLogState): React.ReactNode | null {
  // Pure routing logic lives in resolveCounterpartKind (testable, lives
  // in SceneRouterHelpers); this function only maps the resolved
  // discriminator to a React element. Keeping the dispatch trivial
  // means the perspective-routing test suite covers ~all regressions.
  const kind = resolveCounterpartKind(screen, perspective);
  switch (kind) {
    case "smart-logistics-importer":           return <SmartLogisticsImporter ev={ev} />;
    case "settlement-importer-t1":             return <SettlementImporter ev={ev} variant="t1" />;
    case "settlement-importer-t3":             return <SettlementImporter ev={ev} variant="t3" />;
    case "catalogue-analytics":                return <CatalogueAnalytics />;
    case "inbound-proposal-triage":            return <InboundProposalTriage ev={ev} />;
    case "lc-advised-to-you":                  return <LcAdvisedToYou lc={ev.lcIssuance} />;
    case "trade-in-progress:onboarding":           return <TradeInProgressExporter ev={ev} mode="onboarding" />;
    case "trade-in-progress:escrow-in-progress":   return <TradeInProgressExporter ev={ev} mode="escrow-in-progress" />;
    case "trade-in-progress:lc-in-progress":       return <TradeInProgressExporter ev={ev} mode="lc-in-progress" />;
    case "trade-in-progress:t3-lc-in-progress":    return <TradeInProgressExporter ev={ev} mode="t3-lc-in-progress" />;
    case null:                                 return null;
  }
}

function pickHighlight(screen: string): "Profile" | "GLEIF" | "Counterparty" | "Risk" | "IdentityVerification" | "BillingAndInvoices" | "TradePipeline" | "Logistics" | "ReconciliationDocs" | "ReconciliationSettlement" | "EInvoicing" | "CrossBorderCompliance" | undefined {
  // One side-nav module per screen, picked by what the screen is
  // primarily ABOUT. Acts 0.x (discovery) + bridge interstitials
  // (3.7, 4.18) deliberately stay undefined — the dashboard isn't
  // yet narratively active for those.
  switch (screen) {
    // T1 · Act 1 — onboarding
    case "1.1": return "Profile";
    case "1.2": return "GLEIF";
    case "1.3": return "Risk";
    case "1.4": return "IdentityVerification";
    case "1.5": return "TradePipeline";          // Real-Time catalogue
    case "1.6": return "TradePipeline";          // Real-Time catalogue + proposal popup
    case "1.7": return "BillingAndInvoices";     // OrbID fee checkout
    case "1.8": return "Profile";                // OrbID materialisation → identity now stable

    // T1 · Act 2 — trace
    case "2.1": return "TradePipeline";          // contract capture
    case "2.2": return "ReconciliationDocs";     // document carousel  (was TradePipeline)
    case "2.3": return "ReconciliationDocs";     // reconciliation matrix
    case "2.4": return "EInvoicing";             // invoice FSM
    case "2.5": return "Logistics";              // smart logistics
    case "2.6": return "CrossBorderCompliance";  // compliance snapshot
    case "2.7": return "ReconciliationDocs";     // single-window clearance  (was TradePipeline)

    // T1 · Act 3 — finance (escrow)
    case "3.1": return "ReconciliationSettlement"; // Programmable Escrow composer
    case "3.2": return "BillingAndInvoices";       // funding tile
    case "3.3": return "CrossBorderCompliance";    // gate cascade (compliance gates)
    case "3.4": return "ReconciliationSettlement"; // escrow issued
    case "3.5": return "ReconciliationSettlement"; // settlement simulation
    case "3.6": return "Counterparty";             // T1 reveal — reputation born

    // T2 · Act 4 — bank-financed repeat
    case "4.1": return "Counterparty";              // welcome-back (reputation context)  (was TradePipeline)
    case "4.2": return "TradePipeline";             // reorder draft
    case "4.3": return "TradePipeline";             // counter-offer + accept
    case "4.4": return "Logistics";                 // logistics + insurance picker  (was CrossBorderCompliance)
    case "4.5": return "TradePipeline";             // contract capture
    case "4.6": return "ReconciliationDocs";        // document carousel  (was ReconciliationSettlement)
    case "4.7": return "ReconciliationDocs";        // reconciliation matrix  (was Counterparty)
    case "4.8": return "EInvoicing";                // invoice FSM
    case "4.9": return "CrossBorderCompliance";     // compliance snapshot
    case "4.10": return "CrossBorderCompliance";    // tariff estimate
    case "4.11": return "ReconciliationSettlement"; // LC composer
    case "4.12": return "CrossBorderCompliance";    // gate cascade
    case "4.13": return "ReconciliationSettlement"; // LC issued
    case "4.14": return "Logistics";                // smart logistics
    case "4.15": return "Logistics";                // insurance cert anchor
    case "4.16": return "ReconciliationSettlement"; // tariff final + settlement
    case "4.17": return "Counterparty";             // T2 reveal — reputation compounds

    // T3 · Act 5 — reverse-corridor
    case "5.1": return "Counterparty";              // Pampagro RFQ
    case "5.2": return "TradePipeline";             // Gulf Phosphate reply
    case "5.3": return "CrossBorderCompliance";     // reverse-corridor discovery
    case "5.4": return "Logistics";                 // logistics + insurance picker  (was TradePipeline)
    case "5.5": return "TradePipeline";             // contract capture  (was CrossBorderCompliance)
    case "5.6": return "ReconciliationDocs";        // document carousel  (was TradePipeline)
    case "5.7": return "ReconciliationDocs";        // reconciliation matrix  (was ReconciliationSettlement)
    case "5.8": return "EInvoicing";                // invoice FSM
    case "5.9":  return "CrossBorderCompliance";    // compliance snapshot
    case "5.10": return "CrossBorderCompliance";    // tariff estimate
    case "5.11": return "ReconciliationSettlement"; // LC composer
    case "5.12": return "CrossBorderCompliance";    // gate cascade
    case "5.13": return "ReconciliationSettlement"; // LC issued
    case "5.14": return "Logistics";                // smart logistics (post-LC)
    case "5.15": return "Logistics";                // insurance cert anchor (post-LC)
    case "5.16": return "ReconciliationSettlement"; // tariff final
    case "5.17": return "ReconciliationSettlement"; // settlement
    case "5.18": return "Counterparty";             // T3 reveal

    default:    return undefined;
  }
}

// pickLvcPhase + pickPaymentState moved to lib/scenes/act1 (consumers
// of these helpers — Act 1.4 + 1.7 — live there now).
// pickInvoiceState + lcGateDecisions + scenarioReputation/T3 extracted
// to ./SceneRouterHelpers (M1).
